import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RootState} from '../../../hooks/store';
import {
  RainbowResponseChannel,
  RainbowResponseVenue,
} from '../../../../server/rainbow/types';
import {AvailabilityApiResponse} from '../../../../server/routes/availability/types';
import {composeFetchApiAvailability} from '../../../api/availability';
import {fetchVenueInformation} from '../venue/slice';
import {getUrlInfo} from '../../../utils/getUrlInfo';

import {DATE_FORMAT, fetchDateLocale} from '../../../../shared/utils/date';
import {fetchChannel} from '../channel/slice';
import {getLanguageCode} from '../../../../shared/utils/getHeaderLanguageCode';
import {ANY_EMPLOYEE} from '../constants';
import {isRequestPending, selectInitialDate} from '../selectors';
import {
  apiResponse,
  dateSelect,
  employeeMultiSelect,
  employeeSelect,
  startTimeSelect,
} from '../reducers';
import {handleUnexpectedResourceAccess} from '../../../utils/handleUnexpectedResourceAccess';
import dayjs from 'dayjs';
import {requestConfig} from '../config/slice';
import {Config} from '../../../../shared/types';
import {getSelectedServicesData} from '../../../utils/getSelectedServicesData';
import {isAvailabiltyRoute, isRescheduleRoute} from '../../../utils/isRoute';
import {captureException} from '@treatwell/ui/datadog';
import {initialiseDatadogWithConfig} from '../../../datadog/initialiseClient';

export type Status = 'idle' | 'pending' | 'fulfilled' | 'rejected';

export type AvailabilityState = {
  availabilityStatusRequest: Status;
  appStatusRequest: Status;
  paginationFutureStatusRequest: Status;
  data: AvailabilityApiResponse;
  selected: {
    initialDate: string;
    date: string;
    employee: number | typeof ANY_EMPLOYEE;
    employeeMultiSelect: {[key: string]: number | undefined};
    startTime: string | null;
    hasDiscount: boolean;
    totalPrice: string;
    originalPrice?: string;
  };
};

export const initialise = createAsyncThunk(
  'availability/initialise',
  ({url}: {url: string}, {dispatch}) => {
    const {searchParams} = getUrlInfo(url);
    const proposedServices = searchParams.get('proposedServices');
    const venueId = searchParams.get('venueId') as string;

    if (!venueId) {
      return dispatch(fetchChannel({url}))
        .unwrap()
        .then(() => {
          throw new Error('missing venueId parameter');
        });
    }

    let requests:
      | [
          Promise<RainbowResponseChannel>,
          Promise<AvailabilityApiResponse>,
          Promise<RainbowResponseVenue>,
          Promise<Config>,
        ]
      | [
          Promise<RainbowResponseChannel>,
          Promise<RainbowResponseVenue>,
          Promise<Config>,
        ];

    if (proposedServices !== null) {
      requests = [
        dispatch(fetchChannel({url})).unwrap(),
        dispatch(
          fetchApiAvailability({url, defaultCurrentDate: true}),
        ).unwrap(),
        dispatch(fetchVenueInformation({venueId})).unwrap(),
        dispatch(requestConfig()).unwrap(),
      ];
    } else {
      requests = [
        dispatch(fetchChannel({url})).unwrap(),
        dispatch(fetchVenueInformation({venueId})).unwrap(),
        dispatch(requestConfig()).unwrap(),
      ];
    }

    return Promise.all(requests)
      .then(([channelResponse]) => {
        const {locale} = channelResponse;
        return fetchDateLocale(locale);
      })
      .catch((error) => {
        const isApiError = error.name === 'ApiError';
        return initialiseDatadogWithConfig().then(() => {
          if (!isApiError) {
            captureException(error, []);
          }
          throw error;
        });
      });
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return state.availability.appStatusRequest !== 'pending';
    },
  },
);

export type ApiAvailabilityAction = {
  url: string;
  resetToInitialDate?: boolean;
  startDate?: string | null;
  defaultCurrentDate?: boolean;
  employeeId?: string;
};

export const fetchApiAvailability = createAsyncThunk(
  'availability/fetch',
  (
    {
      url,
      startDate = null,
      resetToInitialDate,
      defaultCurrentDate = false,
      employeeId,
    }: ApiAvailabilityAction,
    {getState},
  ) => {
    const {searchParams, pathname, url: urlPath} = getUrlInfo(url);
    const venueId = searchParams.get('venueId');

    let proposedServices = searchParams.get('proposedServices');
    const state = getState() as RootState;
    const isMultiStaffSelectionEnabled =
      state.availability.data.multiStaffSelectionExperimentEnabled;

    if (isMultiStaffSelectionEnabled && employeeId) {
      if (isAvailabiltyRoute()) {
        proposedServices = getSelectedServicesData(state.availability);
        searchParams.set('proposedServices', proposedServices);
        history.pushState(null, '', urlPath.toString());
      }
      if (isRescheduleRoute()) {
        proposedServices = getSelectedServicesData(state.reschedule);
      }
    }

    if (!startDate) {
      startDate = resetToInitialDate
        ? selectInitialDate(state)
        : searchParams.get('date');
    }
    if (!startDate && defaultCurrentDate) {
      startDate = dayjs(Date.now()).format(DATE_FORMAT);
    }

    const languageCode = getLanguageCode(pathname);
    const getApiAvailability = composeFetchApiAvailability();
    return getApiAvailability({
      venueId,
      proposedServices,
      startDate,
      languageCode,
    });
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return isRequestPending(state);
    },
  },
);

const {url} = getUrlInfo(location.href);
const employeeServiceParam = url.searchParams.get('employeeService');

const getInitialEmployeeSelected = (param: string | null) => {
  if (!param) {
    return ANY_EMPLOYEE;
  }

  const employeeService: Record<string, number> = JSON.parse(param);
  const employeeId = Object.values(employeeService);
  return employeeId[0];
};

const getInitialEmployeeMultiSelected = (param: string | null) => {
  if (!param) {
    return {};
  }

  const employeeService: Record<string, number> = JSON.parse(param);
  return employeeService;
};

const initialState: AvailabilityState = {
  availabilityStatusRequest: 'idle',
  appStatusRequest: 'idle',
  paginationFutureStatusRequest: 'idle',
  data: {
    employees: {},
    days: {},
    services: [],
    skus: {},
    firstIncludedDate: '',
    lastIncludedDate: '',
    moreDaysInThePast: false,
    moreDaysInTheFuture: false,
    multiStaffSelectionExperimentEnabled: false,
    employeeSelectionAllowed: true,
  },
  selected: {
    initialDate: '',
    date: '',
    // when users get redirected from lookbook to datetime, they will
    // have a preselected employee in the url. that is meant to allow
    // the user to book an employee's specific look. the query param
    // `employeeService` will contain the service id and the employee id.
    // we will be using those ids to ensure the EmployeeSelect dropdown
    // has the correct employee preselected. this logic also applies
    // to multi employee select
    employee: getInitialEmployeeSelected(employeeServiceParam),
    startTime: null,
    employeeMultiSelect: getInitialEmployeeMultiSelected(employeeServiceParam),
    totalPrice: '',
    hasDiscount: false,
  },
};

// related to lookbook and having `employeeService` param in the url.
// after we populate the state with the initial selected employee, we are
// removing the query param as we don't keep the employee selection stateful
if (employeeServiceParam) {
  url.searchParams.delete('employeeService');
  history.replaceState(null, '', url.toString());
}

export const availabilitySlice = createSlice({
  name: 'availability',
  initialState,
  reducers: {
    dateSelectAvailability: dateSelect,
    employeeSelectAvailability: employeeSelect,
    employeeMultiSelectAvailability: employeeMultiSelect,
    startTimeSelectAvailability: startTimeSelect,
    resetAvailability(state) {
      state.data = initialState.data;
      state.availabilityStatusRequest = initialState.availabilityStatusRequest;
      state.selected = initialState.selected;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchApiAvailability.fulfilled, apiResponse);
    builder.addCase(fetchApiAvailability.pending, (state, action) => {
      if (action.meta.arg.startDate) {
        state.paginationFutureStatusRequest = 'pending';
      }
      state.availabilityStatusRequest = 'pending';
    });
    builder.addCase(fetchApiAvailability.rejected, (state, action) => {
      if (action.meta.arg.startDate) {
        if (action.error.message) {
          handleUnexpectedResourceAccess({
            errorMessage: action.error.message,
            onMatchedError: () => {
              // rainbow throws this error when we request a startDate that
              // does not exist in our database, which means there is no more
              // availability to paginate through. Therefore we set the pagination
              // status request to fulfilled so that the UI can hide the navigate
              // next arrow
              state.paginationFutureStatusRequest = 'fulfilled';
            },
            onAnyOtherError: () => {
              state.paginationFutureStatusRequest = 'rejected';
            },
          });
        }
      }
      state.availabilityStatusRequest = 'rejected';
    });

    // this state is used to track when to render the entire app
    // all other actions and other dependencies (locale etc) will be
    // ready when this is 'fulfilled'
    builder.addCase(initialise.pending, (state) => {
      state.appStatusRequest = 'pending';
    });
    builder.addCase(initialise.rejected, (state) => {
      state.appStatusRequest = 'rejected';
    });
    builder.addCase(initialise.fulfilled, (state) => {
      state.appStatusRequest = 'fulfilled';
    });
  },
});

export const {
  dateSelectAvailability,
  employeeSelectAvailability,
  employeeMultiSelectAvailability,
  startTimeSelectAvailability,
  resetAvailability,
} = availabilitySlice.actions;
